import * as React from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import type {
  DocumentTree,
  IrrigationFindingsBlock,
  IrrigationRepairRow,
  IrrigationScheduleBlock,
  JobLegendBlock,
  SiteMapBlock,
  TreeScheduleBlock,
  TreeScheduleRow,
} from '@lib/dto/proposal-document.dto'
import { ROAD_DRAW_ORDER } from '@lib/dto/site-features.dto'
import type { FeaturePoint, RoadKind } from '@lib/dto/site-features.dto'
import {
  TREE_PRIORITIES,
  scheduleRowPriority,
} from '@lib/helpers/proposal-plans'
import { parseMoney } from '@lib/helpers/money'
import { treeQuotedForPruning } from '@lib/helpers/proposal-selection'
import { compareTreeLabels } from '@lib/helpers/tree-labels'
import { JOB_TYPE_LABELS, type JobType } from '@lib/enums/job-type.enum'
import {
  PROBLEM_SEVERITY_DESCRIPTIONS,
  PROBLEM_SEVERITY_LABELS,
  type ProblemSeverity,
} from '@lib/enums/problem-severity.enum'
import { UNIDENTIFIED_LABEL } from '@lib/enums/tree-mark.enum'
import { createAerialLayer } from '@/map/basemap'
import { RegionLabelLayer } from '@/map/RegionLabelLayer'
import type { RegionColor } from '@/map/regionColors'
import { JobBadge } from '@/field-map/JobBadge'
import { TreeGlyph } from '@/field-map/TreeGlyph'
import { cn } from '@/lib/utils'
import {
  CANOPY_RADIUS_METERS,
  LENS_LABELS,
  PRIORITY_LABELS,
  PRIORITY_LEGEND,
  canopyClass,
  planAreaColors,
  AREA_WASH_CLASSES,
  jobClass,
  markClass,
  areaClass,
  priorityClass,
  severityClass,
  type Lens,
  type TreeStatus,
} from '@/proposal-document/canopy'
import type { Choosing } from '@/proposal-document/choosing'
import {
  ProposalPhoto,
  ProposalVideo,
  useCanShowPhotos,
} from '@/proposal-document/Photo'
import { useTrack } from '@/proposal-document/activity'
import { scheduleRowId } from '@/proposal-document/anchors'
import {
  placedRepairs,
  repairLabels,
  repairPriority,
  type PlacedRepair,
} from '@/proposal-document/repairs'
import { firstSentence } from '@/proposal-document/text'
import { wholeDollars } from '@/proposal-document/PlanChapter'

/**
 * The site, tree by tree and repair by repair: a plan to look at, a list to
 * scan, and a card for whatever somebody picked, with its photographs and
 * the choice about it.
 *
 * This is where the map stopped being a figure and became the pricing tool.
 * A board member looks at their own property, taps the tree over the pool
 * deck, sees the photo of the crack, and decides; the total beside the page
 * follows. The plan is drawn in the band colors with each canopy at roughly
 * its spread, and the aerial photo is one tap away for anyone who wants to
 * check which building is which.
 *
 * What it keeps from the document it replaced is the scrolling. A document
 * scrolls and a map pans, so the map takes the wheel only once it has been
 * clicked, and on a touch screen one finger scrolls the page.
 */

const FIT_PADDING: L.PointTuple = [28, 28]
const MAX_FIT_ZOOM = 20
const MAP_MAX_ZOOM = 22

/** Smallest mark on screen, so a number always fits inside it. */
const MIN_MARK_PX = 22

const ROAD_WEIGHTS: Record<RoadKind, { road: number; casing: number }> = {
  major: { road: 9, casing: 12 },
  minor: { road: 6, casing: 8 },
  path: { road: 2, casing: 3.5 },
}

const PRIORITY_ORDER = TREE_PRIORITIES

function latLngs(points: FeaturePoint[]): L.LatLngTuple[] {
  return points.map(([latitude, longitude]): L.LatLngTuple => [latitude, longitude])
}

const NO_MEANINGS: ReadonlyMap<JobType, string> = new Map()

/** Screen size of a canopy at this zoom, never smaller than a readable mark. */
function markSize(map: L.Map, tree: DocumentTree): number {
  const metersPerPixel =
    (156_543.033_92 * Math.cos((tree.latitude * Math.PI) / 180)) /
    2 ** map.getZoom()
  return Math.max(
    MIN_MARK_PX,
    Math.round((CANOPY_RADIUS_METERS * 2) / metersPerPixel),
  )
}

function escapeText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

interface MarkLook {
  fill: string
  status: TreeStatus
  selected: boolean
  faded: boolean
}

function markIcon(map: L.Map, tree: DocumentTree, look: MarkLook): L.DivIcon {
  const size = markSize(map, tree)
  const classes = cn(
    'explorer-tree',
    look.status === 'in' ? look.fill : null,
    `explorer-tree--${look.status}`,
    look.selected && 'is-selected',
    look.faded && 'is-faded',
  )
  return L.divIcon({
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<span class="${classes}" style="width:${String(size)}px;height:${String(size)}px"><span>${escapeText(tree.label.replace(/^#/, ''))}</span></span>`,
  })
}

/** A repair's tag, the size of its number, offset when it shares a spot. */
const REPAIR_TAG_HEIGHT = 18
const REPAIR_SPREAD_PX = 26

function repairIcon(entry: PlacedRepair, look: MarkLook): L.DivIcon {
  const width = 8 + entry.label.length * 7
  const classes = cn(
    'explorer-repair',
    look.status === 'in' ? look.fill : null,
    `explorer-repair--${look.status}`,
    look.selected && 'is-selected',
    look.faded && 'is-faded',
  )
  return L.divIcon({
    className: '',
    iconSize: [width, REPAIR_TAG_HEIGHT],
    iconAnchor: [
      width / 2 - entry.dx * REPAIR_SPREAD_PX,
      REPAIR_TAG_HEIGHT / 2 - entry.dy * REPAIR_SPREAD_PX,
    ],
    html: `<span class="${classes}">${escapeText(entry.label)}</span>`,
  })
}

/**
 * What the findings asked the plan to show.
 *
 * Trees and repairs are different highlights. A tree highlight fades every
 * repair; a repair highlight fades every tree.
 */
export type ExplorerHighlight =
  | {
      kind: 'trees'
      site: string
      labels: ReadonlySet<string>
      /** What the highlight shows, e.g. "the 8 trees with Mistletoe". */
      description: string
    }
  | {
      kind: 'repairs'
      site: string
      repairKeys: ReadonlySet<string>
      /** What the highlight shows, e.g. "the 3 repairs for Broken heads". */
      description: string
    }

function treeIsFaded(highlight: ExplorerHighlight | null, label: string): boolean {
  if (highlight === null) return false
  if (highlight.kind === 'repairs') return true
  return !highlight.labels.has(label)
}

function repairIsFaded(highlight: ExplorerHighlight | null, key: string): boolean {
  if (highlight === null) return false
  if (highlight.kind === 'trees') return true
  return !highlight.repairKeys.has(key)
}

/** What is picked: a tree by its label, or a repair by its key. */
type Picked = { kind: 'tree'; label: string } | { kind: 'repair'; key: string }

export interface SiteExplorerProps {
  map: SiteMapBlock
  schedule: TreeScheduleBlock | undefined
  /** The site's irrigation repairs, when the proposal carries any here. */
  irrigation?: IrrigationScheduleBlock | undefined
  /** What each kind of repair is, for the repair's card. */
  irrigationFindings?: IrrigationFindingsBlock | null
  /**
   * What each kind of tree work means, as the appendix explains it: the
   * tree's card says it beside the work, where the reader is looking.
   */
  legend?: JobLegendBlock | null
  /** Null when nobody is choosing: a wording preview, or a locked copy. */
  choosing: Choosing | null
  highlight: ExplorerHighlight | null
  onClearHighlight: () => void
  /** Show trees with no time estimate. Staff only. */
  markUnestimated?: boolean
  /** The number to call about a tree, when the business has one. */
  phone: string | null
  /** Title the explorer by its site when the proposal covers several. */
  showSiteName: boolean
}

export function SiteExplorer({
  map: block,
  schedule,
  irrigation,
  irrigationFindings = null,
  legend = null,
  choosing,
  highlight,
  onClearHighlight,
  markUnestimated = false,
  phone,
  showSiteName,
}: SiteExplorerProps) {
  const rows = React.useMemo(() => schedule?.rows ?? [], [schedule])
  const rowByLabel = React.useMemo(
    () => new Map(rows.map((row) => [row.label, row])),
    [rows],
  )
  // "Next tree" goes by number, as the labels read, not in the order the
  // trees happened to be surveyed.
  const treesByNumber = React.useMemo(
    () => [...block.trees].sort((a, b) => compareTreeLabels(a.label, b.label)),
    [block.trees],
  )
  const workMeanings = React.useMemo(
    () =>
      new Map(
        (legend?.entries ?? []).map((entry) => [
          entry.jobType,
          firstSentence(entry.description),
        ]),
      ),
    [legend],
  )
  const areaNames = React.useMemo(
    () => [...new Set((block.areas ?? []).map((area) => area.name))],
    [block],
  )
  const areaColors = React.useMemo(
    () => planAreaColors(block.areas ?? []),
    [block],
  )
  const pins = React.useMemo(
    () => placedRepairs(block, irrigation),
    [block, irrigation],
  )
  const repairs = React.useMemo(() => irrigation?.repairs ?? [], [irrigation])
  const labelOf = React.useMemo(() => repairLabels(irrigation), [irrigation])

  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const mapRef = React.useRef<L.Map | null>(null)
  const markersRef = React.useRef(new Map<string, L.Marker>())
  const repairMarkersRef = React.useRef(new Map<string, L.Marker>())
  const aerialRef = React.useRef<L.TileLayer | null>(null)
  const [zoom, setZoom] = React.useState(0)

  const [lens, setLens] = React.useState<Lens>('priority')
  const [view, setView] = React.useState<'plan' | 'photo'>('plan')
  const [picked, setPicked] = React.useState<Picked | null>(null)
  const selectedLabel = picked?.kind === 'tree' ? picked.label : null
  const selectedRepair = picked?.kind === 'repair' ? picked.key : null
  const track = useTrack()
  const pickTree = React.useCallback(
    (label: string): void => {
      setPicked({ kind: 'tree', label })
      track({ kind: 'TREE', detail: label })
    },
    [track],
  )
  const pickRepair = React.useCallback((key: string): void => {
    setPicked({ kind: 'repair', key })
  }, [])

  const site = block.siteName
  const statusOf = React.useCallback(
    (label: string): TreeStatus => {
      const row = rowByLabel.get(label)
      if (row === undefined || choosing === null) return 'in'
      return choosing.treeStatus(site, row)
    },
    [choosing, rowByLabel, site],
  )
  const repairStatusOf = React.useCallback(
    (repair: IrrigationRepairRow): TreeStatus =>
      choosing === null ? 'in' : choosing.repairStatus(site, repair),
    [choosing, site],
  )

  const visibleHighlight = highlight?.site === site ? highlight : null

  // Build the map once per block: features, outlines and one marker per tree.
  React.useEffect(() => {
    const container = containerRef.current
    if (container === null) return

    const map = L.map(container, {
      zoomControl: false,
      attributionControl: true,
      maxZoom: MAP_MAX_ZOOM,
      zoomSnap: 0.5,
    })
    mapRef.current = map
    L.control.zoom({ position: 'topleft' }).addTo(map)

    if (window.matchMedia?.('(pointer: coarse)').matches === true) {
      map.dragging.disable()
    }
    map.scrollWheelZoom.disable()
    map.on('click focus', () => {
      map.scrollWheelZoom.enable()
    })
    map.on('blur mouseout', () => {
      map.scrollWheelZoom.disable()
    })
    map.on('zoomend', () => {
      setZoom(map.getZoom())
    })

    const bounds = L.latLngBounds([])
    const features = block.boundary === null ? null : block.features

    if (block.boundary !== null) {
      const ring = block.boundary.vertices.map((v): L.LatLngTuple => [
        v.latitude,
        v.longitude,
      ])
      L.polygon(ring, { className: 'plan-site', stroke: false, interactive: false }).addTo(map)
      bounds.extend(ring)
    }

    if (features !== null) {
      for (const ring of features.water) {
        L.polygon(latLngs(ring), { className: 'plan-water', interactive: false }).addTo(map)
      }
      for (const kind of ROAD_DRAW_ORDER) {
        for (const road of features.roads.filter((entry) => entry.kind === kind)) {
          const points = latLngs(road.points)
          L.polyline(points, {
            className: `plan-road-casing plan-road--${kind}`,
            weight: ROAD_WEIGHTS[kind].casing,
            interactive: false,
          }).addTo(map)
          L.polyline(points, {
            className: `plan-road plan-road--${kind}`,
            weight: ROAD_WEIGHTS[kind].road,
            interactive: false,
          }).addTo(map)
        }
      }
      for (const ring of features.buildings) {
        L.polygon(latLngs(ring), { className: 'plan-building', interactive: false }).addTo(map)
      }
      map.attributionControl.addAttribution('© OpenStreetMap contributors')
    }

    for (const area of block.areas ?? []) {
      const ring = area.boundary.vertices.map((v): L.LatLngTuple => [
        v.latitude,
        v.longitude,
      ])
      L.polygon(ring, { className: 'plan-area', weight: 1.5, dashArray: '5 4', interactive: false }).addTo(map)
      bounds.extend(ring)
    }

    for (const zone of block.irrigation?.zones ?? []) {
      const ring = zone.boundary.vertices.map((v): L.LatLngTuple => [
        v.latitude,
        v.longitude,
      ])
      L.polygon(ring, {
        className: 'plan-zone',
        weight: 1.5,
        dashArray: '2 4',
        fillOpacity: 0.18,
        interactive: false,
      }).addTo(map)
      bounds.extend(ring)
    }

    if (block.boundary !== null) {
      L.polygon(
        block.boundary.vertices.map((v): L.LatLngTuple => [v.latitude, v.longitude]),
        { className: 'plan-boundary', weight: 2.5, fill: false, interactive: false },
      ).addTo(map)
    }

    const markers = new Map<string, L.Marker>()
    for (const tree of block.trees) {
      const position: L.LatLngTuple = [tree.latitude, tree.longitude]
      const marker = L.marker(position, {
        icon: L.divIcon({ className: '', html: '' }),
        keyboard: true,
        title: `Tree ${tree.label}`,
        riseOnHover: true,
      })
      marker.on('click', () => {
        pickTree(tree.label)
      })
      marker.addTo(map)
      markers.set(tree.label, marker)
      bounds.extend(position)
    }
    markersRef.current = markers

    const repairMarkers = new Map<string, L.Marker>()
    for (const entry of pins) {
      const position: L.LatLngTuple = [entry.latitude, entry.longitude]
      const marker = L.marker(position, {
        icon: L.divIcon({ className: '', html: '' }),
        keyboard: true,
        title: `Repair ${entry.label}: ${entry.repair.kind}`,
        riseOnHover: true,
      })
      marker.on('click', () => {
        pickRepair(entry.key)
      })
      marker.addTo(map)
      repairMarkers.set(entry.key, marker)
      bounds.extend(position)
    }
    repairMarkersRef.current = repairMarkers

    const fit = (): void => {
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: FIT_PADDING, maxZoom: MAX_FIT_ZOOM })
      } else if (block.center !== null) {
        map.setView([block.center.latitude, block.center.longitude], 18)
      }
    }
    fit()
    setZoom(map.getZoom())

    // Once somebody has moved the map it is theirs; a resize must not snatch
    // it back to the whole site.
    let interacted = false
    const markInteracted = (): void => {
      interacted = true
    }
    for (const name of ['pointerdown', 'wheel', 'keydown'] as const) {
      container.addEventListener(name, markInteracted, { passive: true })
    }
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            map.invalidateSize()
            if (!interacted) fit()
          })
    observer?.observe(container)

    return () => {
      observer?.disconnect()
      for (const name of ['pointerdown', 'wheel', 'keydown'] as const) {
        container.removeEventListener(name, markInteracted)
      }
      map.remove()
      mapRef.current = null
      aerialRef.current = null
      markersRef.current = new Map()
      repairMarkersRef.current = new Map()
    }
  }, [block, pins, pickTree, pickRepair])

  // Repaint the marks whenever what they show changes.
  React.useEffect(() => {
    const map = mapRef.current
    if (map === null) return
    for (const tree of block.trees) {
      const marker = markersRef.current.get(tree.label)
      const row = rowByLabel.get(tree.label)
      if (marker === undefined) continue
      const areaColor =
        row?.areaName == null ? null : (areaColors.get(row.areaName) ?? null)
      marker.setIcon(
        markIcon(map, tree, {
          fill:
            row === undefined
              ? 'canopy--plain'
              : canopyClass(lens, row, areaColor),
          status: statusOf(tree.label),
          selected: selectedLabel === tree.label,
          faded: treeIsFaded(visibleHighlight, tree.label),
        }),
      )
      marker.setZIndexOffset(selectedLabel === tree.label ? 1000 : 0)
    }
  }, [block, lens, rowByLabel, selectedLabel, statusOf, visibleHighlight, areaColors, zoom])

  /*
   * Under the Areas view, each area washed in its color and named, so a
   * client reads which area is which rather than matching swatches to a
   * legend (docs/map-regions.md). Drawn only then: on the other views the
   * colors would say something about the trees that is not true.
   */
  React.useEffect(() => {
    const map = mapRef.current
    if (map === null || lens !== 'areas') return undefined
    const washes = L.layerGroup().addTo(map)
    for (const area of block.areas ?? []) {
      const color = areaColors.get(area.name)
      if (color === undefined) continue
      L.polygon(
        area.boundary.vertices.map((v): L.LatLngTuple => [v.latitude, v.longitude]),
        { className: AREA_WASH_CLASSES[color], stroke: false, interactive: false },
      ).addTo(washes)
    }
    const names = new RegionLabelLayer(map, { overMarkers: true })
    names.set(
      (block.areas ?? []).map((area) => ({
        key: area.id,
        ring: area.boundary.vertices,
        label: { kind: 'name', text: area.name, className: 'explorer-area-name' },
      })),
    )
    return () => {
      washes.remove()
      names.remove()
    }
    // The map is built again when the block, the pins or the pickers change,
    // and these go on the new one.
  }, [block, pins, pickTree, pickRepair, lens, areaColors])

  // The repairs' tags, colored by urgency under the priority lens.
  React.useEffect(() => {
    for (const entry of pins) {
      const marker = repairMarkersRef.current.get(entry.key)
      if (marker === undefined) continue
      marker.setIcon(
        repairIcon(entry, {
          fill:
            lens === 'priority'
              ? priorityClass(repairPriority(entry.repair))
              : 'canopy--water',
          status: repairStatusOf(entry.repair),
          selected: selectedRepair === entry.key,
          faded: repairIsFaded(visibleHighlight, entry.key),
        }),
      )
      marker.setZIndexOffset(selectedRepair === entry.key ? 1000 : 500)
    }
  }, [pins, lens, repairStatusOf, selectedRepair, visibleHighlight, zoom])

  // The aerial photo, on request.
  React.useEffect(() => {
    const map = mapRef.current
    if (map === null) return
    if (view === 'photo') {
      aerialRef.current ??= createAerialLayer()
      aerialRef.current.addTo(map)
    } else if (aerialRef.current !== null) {
      aerialRef.current.remove()
    }
  }, [view, block])

  // Keep whatever is picked in view.
  React.useEffect(() => {
    const map = mapRef.current
    if (map === null || picked === null) return
    const marker =
      picked.kind === 'tree'
        ? markersRef.current.get(picked.label)
        : repairMarkersRef.current.get(picked.key)
    if (marker === undefined) return
    map.panInside(marker.getLatLng(), { padding: [60, 60] })
  }, [picked])

  // A highlight from the findings frames those trees.
  React.useEffect(() => {
    const map = mapRef.current
    if (map === null || visibleHighlight === null) return
    const bounds = L.latLngBounds([])
    if (visibleHighlight.kind === 'trees') {
      for (const tree of block.trees) {
        if (visibleHighlight.labels.has(tree.label)) {
          bounds.extend([tree.latitude, tree.longitude])
        }
      }
    } else {
      for (const entry of pins) {
        if (visibleHighlight.repairKeys.has(entry.key)) {
          bounds.extend([entry.latitude, entry.longitude])
        }
      }
    }
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [70, 70], maxZoom: 20 })
    }
  }, [visibleHighlight, block, pins])

  const selectedTree =
    selectedLabel === null
      ? null
      : (block.trees.find((tree) => tree.label === selectedLabel) ?? null)
  const selectedRow =
    selectedLabel === null ? undefined : rowByLabel.get(selectedLabel)

  const selectedRepairRow =
    selectedRepair === null
      ? undefined
      : repairs.find((repair) => repair.key === selectedRepair)

  function step(offset: number): void {
    const count = treesByNumber.length
    if (count === 0) return
    const index = treesByNumber.findIndex((tree) => tree.label === selectedLabel)
    const next = treesByNumber[(index + offset + count) % count]
    if (next !== undefined) pickTree(next.label)
  }

  function stepRepair(offset: number): void {
    if (repairs.length === 0) return
    const index = repairs.findIndex((repair) => repair.key === selectedRepair)
    const next = repairs[(index + offset + repairs.length) % repairs.length]
    if (next !== undefined) pickRepair(next.key)
  }

  const hasTrees = block.trees.length > 0
  const lenses: Lens[] =
    areaNames.length > 0
      ? ['priority', 'work', 'species', 'areas']
      : ['priority', 'work', 'species']

  /** The card for whatever is picked, or the invitation to pick something. */
  function card(): React.ReactNode {
    if (selectedRepairRow !== undefined) {
      return (
        <RepairCard
          site={site}
          repair={selectedRepairRow}
          label={labelOf.get(selectedRepairRow.key) ?? ''}
          isPinned={pins.some((entry) => entry.key === selectedRepairRow.key)}
          description={
            irrigationFindings?.entries.find(
              (entry) => entry.name === selectedRepairRow.kind,
            )?.description ?? null
          }
          choosing={choosing}
          status={repairStatusOf(selectedRepairRow)}
          phone={phone}
          onPrevious={() => {
            stepRepair(-1)
          }}
          onNext={() => {
            stepRepair(1)
          }}
          onClose={() => {
            setPicked(null)
          }}
        />
      )
    }
    if (selectedTree === null || selectedRow === undefined) {
      return (
        <EmptyCard
          rows={rows}
          repairs={repairs}
          labelOf={labelOf}
          onSelect={pickTree}
          onSelectRepair={pickRepair}
        />
      )
    }
    return (
      <TreeCard
        site={site}
        tree={selectedTree}
        row={selectedRow}
        choosing={choosing}
        status={statusOf(selectedTree.label)}
        workMeanings={workMeanings}
        phone={phone}
        onPrevious={() => {
          step(-1)
        }}
        onNext={() => {
          step(1)
        }}
        onClose={() => {
          setPicked(null)
        }}
      />
    )
  }

  return (
    <div className="explorer">
      <div className="explorer-head">
        <div className="explorer-title">
          <h3 className="proposal-h3">
            {showSiteName ? block.siteName : explorerTitle(hasTrees, repairs.length > 0)}
          </h3>
          <p className="proposal-small">
            {countWords(block.trees.length, repairs.length)}
            {block.acreage === null ? '' : ` · ${block.acreage} acres`}. Pick{' '}
            {pickWords(hasTrees, repairs.length > 0)} to see its photos and what
            we would do.
          </p>
        </div>
        <div className="explorer-controls">
          {/* The lenses ask about trees; with none, repairs show their urgency. */}
          {!hasTrees ? null : (
            <div className="proposal-seg" role="group" aria-label="Color the plan by">
              {lenses.map((entry) => (
                <button
                  key={entry}
                  type="button"
                  aria-pressed={lens === entry}
                  onClick={() => {
                    setLens(entry)
                  }}
                >
                  {LENS_LABELS[entry]}
                </button>
              ))}
            </div>
          )}
          <div className="proposal-seg" role="group" aria-label="Show the site as">
            <button type="button" aria-pressed={view === 'plan'} onClick={() => { setView('plan') }}>
              Plan
            </button>
            <button type="button" aria-pressed={view === 'photo'} onClick={() => { setView('photo') }}>
              Photo
            </button>
          </div>
        </div>
      </div>

      {visibleHighlight === null ? null : (
        <div className="explorer-banner" role="status">
          <span>Showing {visibleHighlight.description}.</span>
          <button type="button" className="proposal-link" onClick={onClearHighlight}>
            Show everything
          </button>
        </div>
      )}

      <div className="explorer-body">
        <div className="explorer-list" aria-label={hasTrees ? 'Trees and repairs' : 'Repairs'}>
          <TreeList
            site={site}
            rows={rows}
            statusOf={statusOf}
            selected={selectedLabel}
            markUnestimated={markUnestimated}
            onSelect={pickTree}
          />
          <RepairList
            repairs={repairs}
            labelOf={labelOf}
            statusOf={repairStatusOf}
            selected={selectedRepair}
            onSelect={pickRepair}
          />
        </div>
        <div className="explorer-map-column">
          {/*
            The photo modifier sits on a wrapper React owns, never on the
            map's own element. Leaflet adds its classes there when it starts
            (leaflet-container, which clips the map and keeps tiles their
            size), and a className React changed on that element would
            replace them: the trees would spill across the page and the
            aerial photo would vanish.
          */}
          <div className={cn('explorer-map-frame', view === 'photo' && 'explorer-map--photo')}>
            <div
              ref={containerRef}
              className="explorer-map"
              role="group"
              aria-label={`Plan of ${block.siteName}. Zoom with the buttons or by pinching, and pick ${pickWords(hasTrees, repairs.length > 0)} to see it.`}
            />
          </div>
          <Legend
            lens={lens}
            block={block}
            rows={rows}
            areaNames={areaNames}
            areaColors={areaColors}
            hasRepairs={pins.length > 0}
          />
        </div>
        <div className="explorer-card-column">{card()}</div>
      </div>
    </div>
  )
}

/** The trees, most urgent first, so a reader starts where the risk is. */
function TreeList({
  site,
  rows,
  statusOf,
  selected,
  markUnestimated,
  onSelect,
}: {
  site: string
  rows: TreeScheduleRow[]
  statusOf: (label: string) => TreeStatus
  selected: string | null
  markUnestimated: boolean
  onSelect: (label: string) => void
}) {
  const groups = PRIORITY_ORDER.map((priority) => ({
    priority,
    rows: rows.filter((row) => scheduleRowPriority(row) === priority),
  })).filter((group) => group.rows.length > 0)

  return (
    <div>
      {groups.map((group) => (
        <div key={group.priority}>
          <p className="explorer-list-head proposal-label">
            {PRIORITY_LABELS[group.priority]} · {group.rows.length}
          </p>
          <ul>
            {group.rows.map((row) => {
              const status = statusOf(row.label)
              const topProblem = [...row.problems].sort(
                (a, b) => (b.severity ?? 0) - (a.severity ?? 0),
              )[0]
              return (
                <li key={row.label} id={scheduleRowId(site, row.label)}>
                  <button
                    type="button"
                    className={cn('explorer-row', status === 'out' && 'is-out')}
                    aria-current={selected === row.label}
                    onClick={() => {
                      onSelect(row.label)
                    }}
                  >
                    <span className="explorer-row-num proposal-mono">
                      {row.label}
                    </span>
                    <span className="explorer-row-text">
                      <span className="explorer-row-name">
                        <TreeGlyph species={row.species} size={13} />
                        {row.species?.name ?? UNIDENTIFIED_LABEL}
                      </span>
                      <span className="explorer-row-sub">
                        {topProblem?.name ??
                          (row.noPruningRecommended === true
                            ? 'No pruning recommended'
                            : 'Routine care')}
                      </span>
                    </span>
                    <span className="explorer-row-state">
                      {markUnestimated && row.estimatedLaborMinutes === null
                        ? 'No estimate'
                        : statusWord(status, row.isOptional)}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </div>
  )
}

function statusWord(status: TreeStatus, isOptional: boolean): string {
  if (isOptional) return status === 'in' ? 'Optional · in' : 'Optional'
  if (status === 'out') return 'Left out'
  return ''
}

/** What the colors mean under the current lens. */
function Legend({
  lens,
  block,
  rows,
  areaNames,
  areaColors,
  hasRepairs,
}: {
  lens: Lens
  block: SiteMapBlock
  rows: TreeScheduleRow[]
  areaNames: string[]
  areaColors: ReadonlyMap<string, RegionColor>
  hasRepairs: boolean
}) {
  let items: { className: string; label: string }[]
  if (lens === 'priority') {
    items = PRIORITY_ORDER.map((priority) => ({
      className: priorityClass(priority),
      label: PRIORITY_LEGEND[priority],
    }))
  } else if (lens === 'work') {
    const types = [
      ...new Set(rows.flatMap((row) => row.jobs.map((job) => job.type))),
    ].filter((type) => type !== 'OTHER')
    items = types.map((type) => ({
      className: jobClass(type),
      label: JOB_TYPE_LABELS[type],
    }))
  } else if (lens === 'species') {
    const seen = new Map<string, { className: string; label: string }>()
    for (const tree of block.trees) {
      const name = tree.species?.name ?? UNIDENTIFIED_LABEL
      if (!seen.has(name)) {
        seen.set(name, {
          className: markClass(tree.species?.color ?? 'GREY'),
          label: name,
        })
      }
    }
    items = [...seen.values()]
  } else {
    items = areaNames.map((name) => ({
      className: areaClass(areaColors.get(name) ?? 'yellow'),
      label: name,
    }))
  }

  return (
    <ul className="explorer-legend" aria-label="Legend">
      {items.map((item) => (
        <li key={item.label}>
          <span className={cn('explorer-swatch', item.className)} aria-hidden />
          {item.label}
        </li>
      ))}
      {hasRepairs ? (
        <li>
          <span className="explorer-swatch explorer-swatch--repair" aria-hidden />
          Irrigation repair, numbered R1, R2…
        </li>
      ) : null}
      <li>
        <span className="explorer-swatch explorer-swatch--out" aria-hidden />
        Left out
      </li>
    </ul>
  )
}

/**
 * Before anything is picked: an invitation, starting with the worst tree, or
 * the worst repair when there are no trees.
 */
function EmptyCard({
  rows,
  repairs,
  labelOf,
  onSelect,
  onSelectRepair,
}: {
  rows: TreeScheduleRow[]
  repairs: IrrigationRepairRow[]
  labelOf: ReadonlyMap<string, string>
  onSelect: (label: string) => void
  onSelectRepair: (key: string) => void
}) {
  const worst = [...rows].sort(
    (a, b) =>
      Math.max(0, ...b.problems.map((p) => p.severity ?? 0)) -
      Math.max(0, ...a.problems.map((p) => p.severity ?? 0)),
  )[0]
  const worstRepair = [...repairs].sort(
    (a, b) => (b.severity ?? 0) - (a.severity ?? 0),
  )[0]

  if (worst === undefined && worstRepair !== undefined) {
    return (
      <div className="explorer-card explorer-card--empty">
        <p className="proposal-label">Pick a repair</p>
        <p>
          Tap any tag on the plan, or a repair in the list, to see what we
          found and what we would do.
        </p>
        <button
          type="button"
          className="proposal-btn proposal-btn--outline"
          onClick={() => {
            onSelectRepair(worstRepair.key)
          }}
        >
          Start with repair {labelOf.get(worstRepair.key)}
        </button>
      </div>
    )
  }
  return (
    <div className="explorer-card explorer-card--empty">
      <p className="proposal-label">Pick a tree</p>
      <p>
        Tap any circle on the plan, or a tree in the list, to see its photos,
        what we found and what we would do.
        {repairs.length > 0 ? ' The tags are irrigation repairs.' : ''}
      </p>
      {worst === undefined ? null : (
        <button
          type="button"
          className="proposal-btn proposal-btn--outline"
          onClick={() => {
            onSelect(worst.label)
          }}
        >
          Start with tree {worst.label}
        </button>
      )}
    </div>
  )
}

/** The repairs, in their numbered order, under the trees. */
function RepairList({
  repairs,
  labelOf,
  statusOf,
  selected,
  onSelect,
}: {
  repairs: IrrigationRepairRow[]
  labelOf: ReadonlyMap<string, string>
  statusOf: (repair: IrrigationRepairRow) => TreeStatus
  selected: string | null
  onSelect: (key: string) => void
}) {
  if (repairs.length === 0) return null
  return (
    <div>
      <p className="explorer-list-head proposal-label">
        Irrigation repairs · {repairs.length}
      </p>
      <ul>
        {repairs.map((repair) => {
          const status = statusOf(repair)
          return (
            <li key={repair.key}>
              <button
                type="button"
                className={cn('explorer-row', status === 'out' && 'is-out')}
                aria-current={selected === repair.key}
                onClick={() => {
                  onSelect(repair.key)
                }}
              >
                <span className="explorer-row-num proposal-mono">
                  {labelOf.get(repair.key)}
                </span>
                <span className="explorer-row-text">
                  <span className="explorer-row-name">{repair.kind}</span>
                  <span className="explorer-row-sub">{repair.subject}</span>
                </span>
                <span className="explorer-row-state">{statusWord(status, false)}</span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/**
 * One repair, as the proposal describes it, and the choice about it. Like a
 * tree's card, a description first and never a price list: no hours, no
 * parts, no rate.
 */
function RepairCard({
  site,
  repair,
  label,
  isPinned,
  description,
  choosing,
  status,
  phone,
  onPrevious,
  onNext,
  onClose,
}: {
  site: string
  repair: IrrigationRepairRow
  label: string
  /** Whether it is on the plan; one on the system as a whole is not. */
  isPinned: boolean
  /** The kind's client-facing description, from the findings. */
  description: string | null
  choosing: Choosing | null
  status: TreeStatus
  phone: string | null
  onPrevious: () => void
  onNext: () => void
  onClose: () => void
}) {
  const canShowPhotos = useCanShowPhotos()
  const photo = canShowPhotos ? repair.photoDocumentId : null

  return (
    <article className="explorer-card" aria-label={`Repair ${label}`}>
      {photo === null ? null : (
        <figure className="explorer-photo">
          <ProposalPhoto documentId={photo} alt={`Survey photo of repair ${label}: ${repair.kind}`} />
          <figcaption className="proposal-chip">
            Repair {label} · {repair.subject}
          </figcaption>
        </figure>
      )}

      <div className="explorer-card-body">
        <div className="explorer-card-head">
          <div>
            <p className="proposal-tag">{repair.areaName ?? site}</p>
            <h4 className="proposal-h3">
              {label} · {repair.kind}
            </h4>
          </div>
          <button type="button" className="proposal-link" onClick={onClose}>
            Close
          </button>
        </div>

        {repair.severity === null ? null : (
          <p className="explorer-severity">
            <SeverityChip severity={repair.severity} />
            <span>{PROBLEM_SEVERITY_DESCRIPTIONS[repair.severity]}</span>
          </p>
        )}

        <p>
          <span className="proposal-label">Where</span> {repair.subject}
          {repair.count > 1 ? (
            <>
              {' · '}
              <span className="proposal-label">How many</span> {repair.count}
            </>
          ) : null}
        </p>
        {description === null ? null : <p className="proposal-muted">{description}</p>}
        {isPinned ? null : (
          <p className="proposal-small">
            Not marked on the plan: it is on the system as a whole.
          </p>
        )}

        {choosing === null ? null : (
          <RepairChoice
            site={site}
            repair={repair}
            label={label}
            status={status}
            choosing={choosing}
          />
        )}

        {phone === null ? null : (
          <p className="proposal-small">
            Questions about this repair? Call{' '}
            <a className="proposal-mono" href={`tel:${phone.replace(/[^0-9+]/g, '')}`}>
              {phone}
            </a>
            .
          </p>
        )}

        <div className="explorer-step">
          <button type="button" className="proposal-link" onClick={onPrevious}>
            ← Previous repair
          </button>
          <button type="button" className="proposal-link" onClick={onNext}>
            Next repair →
          </button>
        </div>
      </div>
    </article>
  )
}

/** The decision about one repair, as a tree's is made. */
function RepairChoice({
  site,
  repair,
  label,
  status,
  choosing,
}: {
  site: string
  repair: IrrigationRepairRow
  label: string
  status: TreeStatus
  choosing: Choosing
}) {
  const [said, setSaid] = React.useState<number | null>(null)
  React.useEffect(() => {
    setSaid(null)
  }, [repair.key])

  const reason = choosing.repairGroupReason(site, repair)
  if (reason !== null && status === 'out') {
    return (
      <p className="explorer-note">
        Left out with {reason}. Put {reason} back under “Adjust what’s
        included” to choose this repair on its own.
      </p>
    )
  }

  const options: { value: 'in' | 'out'; label: string; current: boolean }[] = [
    { value: 'in', label: 'Do the repair', current: status === 'in' },
    { value: 'out', label: 'Leave it for now', current: status === 'out' },
  ]

  function pick(value: 'in' | 'out'): void {
    setSaid(parseMoney(choosing.outcome.totalAmount) ?? 0)
    choosing.setRepair(site, repair, value)
  }

  return (
    <div className="explorer-choice">
      <div className="proposal-choice" role="radiogroup" aria-label={`Your choice for repair ${label}`}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={option.current}
            disabled={choosing.isLocked}
            onClick={() => {
              pick(option.value)
            }}
          >
            {option.label}
          </button>
        ))}
      </div>
      <ChangeNote beforeCents={said} after={choosing.outcome.totalAmount} />
    </div>
  )
}

/** The explorer's heading when it is not titled by its site. */
function explorerTitle(hasTrees: boolean, hasRepairs: boolean): string {
  if (hasTrees && hasRepairs) return 'Every tree and repair, on the plan'
  return hasRepairs ? 'Every repair, on the plan' : 'Every tree, on the plan'
}

/** "40 trees and 6 repairs", leaving out whichever there are none of. */
function countWords(trees: number, repairs: number): string {
  const treeWords = `${String(trees)} ${trees === 1 ? 'tree' : 'trees'}`
  const repairWords = `${String(repairs)} irrigation ${repairs === 1 ? 'repair' : 'repairs'}`
  if (repairs === 0) return treeWords
  return trees === 0 ? repairWords : `${treeWords} and ${repairWords}`
}

/** What a reader can pick: "a tree", "a repair", or "a tree or a repair". */
function pickWords(hasTrees: boolean, hasRepairs: boolean): string {
  if (hasTrees && hasRepairs) return 'a tree or a repair'
  return hasRepairs ? 'a repair' : 'a tree'
}

/**
 * What a tree with nothing wrong with it is told. The arborist's "No pruning
 * recommended" is said as such, rather than promising routine pruning the
 * proposal does not quote.
 */
function healthyWords(row: TreeScheduleRow): string {
  return row.noPruningRecommended === true
    ? 'Healthy. No pruning recommended.'
    : 'Healthy. Routine pruning keeps it that way.'
}

/**
 * Whether this tree offers nothing to take or leave: no pruning recommended,
 * and no removal or problem quoted. "Do the work" on a tree with no work is a
 * choice that changes nothing.
 */
function hasNothingToChoose(row: TreeScheduleRow): boolean {
  return (
    row.noPruningRecommended === true &&
    row.removal === null &&
    row.problems.length === 0
  )
}

function worstOf(tree: DocumentTree): ProblemSeverity | null {
  let worst: ProblemSeverity | null = null
  for (const problem of tree.problems) {
    if (problem.severity !== null && (worst === null || problem.severity > worst)) {
      worst = problem.severity
    }
  }
  return worst
}

/** Every photo of a tree: the tree itself, then each problem's evidence. */
function photosOf(tree: DocumentTree): { id: string; caption: string }[] {
  const photos = tree.photoDocumentIds.map((id) => ({
    id,
    caption: `Tree ${tree.label}`,
  }))
  for (const problem of tree.problems) {
    for (const id of problem.photoDocumentIds) {
      photos.push({ id, caption: `Tree ${tree.label} · ${problem.name}` })
    }
  }
  return photos
}

/**
 * One tree, as the proposal describes it, and the choice about it.
 *
 * No crew hours and no hourly rate, as everywhere else on the client's copy.
 * A choice that moves the price says by how much only once it is made, so
 * the card is a description first and a price list never.
 */
export function TreeCard({
  site,
  tree,
  row,
  choosing,
  status,
  workMeanings = NO_MEANINGS,
  phone,
  onPrevious,
  onNext,
  onClose,
}: {
  site: string
  tree: DocumentTree
  row: TreeScheduleRow
  choosing: Choosing | null
  status: TreeStatus
  /** What each kind of work means, in a sentence; from the proposal's legend. */
  workMeanings?: ReadonlyMap<JobType, string>
  phone: string | null
  onPrevious: () => void
  onNext: () => void
  onClose: () => void
}) {
  const canShowPhotos = useCanShowPhotos()
  const track = useTrack()
  const photos = canShowPhotos ? photosOf(tree) : []
  const [photoIndex, setPhotoIndex] = React.useState(0)
  React.useEffect(() => {
    setPhotoIndex(0)
  }, [tree.label])
  const photo = photos[photoIndex] ?? photos[0]
  const worst = worstOf(tree)

  return (
    <article className="explorer-card" aria-label={`Tree ${tree.label}`}>
      {photo === undefined ? null : (
        <figure className="explorer-photo">
          <ProposalPhoto documentId={photo.id} alt={`Survey photo of ${photo.caption}`} />
          <figcaption className="proposal-chip">
            {photo.caption}
            {photos.length > 1
              ? ` · ${String(photoIndex + 1)} of ${String(photos.length)}`
              : ''}
          </figcaption>
          {photos.length > 1 ? (
            <div className="explorer-photo-nav">
              <button
                type="button"
                aria-label="Previous photo"
                onClick={() => {
                  setPhotoIndex((photoIndex - 1 + photos.length) % photos.length)
                }}
              >
                ‹
              </button>
              <button
                type="button"
                aria-label="Next photo"
                onClick={() => {
                  setPhotoIndex((photoIndex + 1) % photos.length)
                }}
              >
                ›
              </button>
            </div>
          ) : null}
        </figure>
      )}

      <div className="explorer-card-body">
        <div className="explorer-card-head">
          <div>
            <p className="proposal-tag">
              {row.areaName ?? site}
            </p>
            <h4 className="proposal-h3">
              {tree.label} · {tree.species?.name ?? UNIDENTIFIED_LABEL}
            </h4>
          </div>
          <button type="button" className="proposal-link" onClick={onClose}>
            Close
          </button>
        </div>

        {tree.isOptional ? (
          <p className="explorer-note">
            <span className="proposal-strong">Optional work.</span> We offer
            it, but it is not part of what we recommend.
          </p>
        ) : null}

        {worst === null ? null : (
          <p className="explorer-severity">
            <SeverityChip severity={worst} />
            <span>{PROBLEM_SEVERITY_DESCRIPTIONS[worst]}</span>
          </p>
        )}

        {tree.problems.length === 0 ? (
          <p>{healthyWords(row)}</p>
        ) : (
          <ul className="explorer-findings">
            {tree.problems.map((problem, index) => (
              // By position as well as name: several "Other" findings share it.
              <li key={`${problem.name}-${String(index)}`}>
                <p className="proposal-strong">
                  {problem.name}
                  {problem.severity === null
                    ? null
                    : ` · ${PROBLEM_SEVERITY_LABELS[problem.severity]}`}
                </p>
                {problem.notes === null ? null : <p>{problem.notes}</p>}
                {problem.description === null ? null : (
                  <p className="proposal-muted">{problem.description}</p>
                )}
                {problem.solutions.length === 0 ? null : (
                  <p>
                    <span className="proposal-label">What we would do</span>{' '}
                    {problem.solutions.map((solution) => solution.label).join(', ')}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}

        {row.noPruningRecommended === true && tree.problems.length > 0 ? (
          <p className="proposal-strong">No pruning recommended.</p>
        ) : null}

        {tree.jobs.length === 0 ? null : (
          <div>
            <p className="proposal-label">The work</p>
            <ul className="explorer-jobs">
              {tree.jobs.map((job) => {
                const meaning = workMeanings.get(job.type)
                return (
                  <li key={`${job.type}-${String(job.level)}`}>
                    <JobBadge type={job.type} level={job.level} height={18} />
                    <span className="explorer-job">
                      <span>
                        {JOB_TYPE_LABELS[job.type]}
                        {job.type === 'OTHER' ? null : (
                          <span className="proposal-muted"> · level {job.level}</span>
                        )}
                      </span>
                      {meaning === undefined ? null : (
                        <span className="explorer-job-meaning">{meaning}</span>
                      )}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {tree.videos.map((video) => (
          <ProposalVideo
            key={video.id}
            videoId={video.id}
            durationSeconds={video.durationSeconds}
            label={`tree ${tree.label}`}
            onPlay={() => {
              track({ kind: 'VIDEO', detail: tree.label })
            }}
          />
        ))}

        {tree.removal === null ? null : (
          <p className="explorer-removal">
            <span className="proposal-strong">Removal was quoted too.</span>{' '}
            {tree.removal.notes ?? 'As an alternative to pruning it.'}
          </p>
        )}

        {choosing === null || hasNothingToChoose(row) ? null : (
          <TreeChoice site={site} row={row} status={status} choosing={choosing} />
        )}

        {phone === null ? null : (
          <p className="proposal-small">
            Questions about this tree? Call{' '}
            <a className="proposal-mono" href={`tel:${phone.replace(/[^0-9+]/g, '')}`}>
              {phone}
            </a>
            .
          </p>
        )}

        <div className="explorer-step">
          <button type="button" className="proposal-link" onClick={onPrevious}>
            ← Previous tree
          </button>
          <button type="button" className="proposal-link" onClick={onNext}>
            Next tree →
          </button>
        </div>
      </div>
    </article>
  )
}

function SeverityChip({ severity }: { severity: ProblemSeverity }) {
  return (
    <span className={cn('proposal-severity', severityClass(severity))}>
      {PROBLEM_SEVERITY_LABELS[severity]}
    </span>
  )
}

/** The decision about one tree, said in the reader's words. */
function TreeChoice({
  site,
  row,
  status,
  choosing,
}: {
  site: string
  row: TreeScheduleRow
  status: TreeStatus
  choosing: Choosing
}) {
  const [said, setSaid] = React.useState<number | null>(null)
  React.useEffect(() => {
    setSaid(null)
  }, [row.label])

  const reason = choosing.groupReason(site, row)
  if (reason !== null && status === 'out') {
    return (
      <p className="explorer-note">
        Left out with {reason}. Put {reason} back under “Adjust what’s
        included” to choose this tree on its own.
      </p>
    )
  }

  const choice = choosing.workChoice(site, row)
  const quotedPruning = treeQuotedForPruning(row)
  const quotedRemoval = row.removal !== null
  let options: { value: 'in' | 'out' | 'prune' | 'remove'; label: string; current: boolean }[]
  if (quotedRemoval && quotedPruning) {
    options = [
      { value: 'prune', label: 'Prune it', current: status === 'in' && choice === 'PRUNE' },
      { value: 'remove', label: 'Remove it', current: status === 'in' && choice === 'REMOVE' },
      { value: 'out', label: 'Leave it for now', current: status === 'out' },
    ]
  } else if (quotedRemoval) {
    options = [
      { value: 'remove', label: 'Remove it', current: status === 'in' },
      { value: 'out', label: 'Leave it for now', current: status === 'out' },
    ]
  } else {
    options = [
      { value: 'in', label: 'Do the work', current: status === 'in' },
      { value: 'out', label: 'Leave it for now', current: status === 'out' },
    ]
  }

  function pick(value: 'in' | 'out' | 'prune' | 'remove'): void {
    setSaid(parseMoney(choosing.outcome.totalAmount) ?? 0)
    choosing.setTree(site, row, value)
  }

  return (
    <div className="explorer-choice">
      <div className="proposal-choice" role="radiogroup" aria-label={`Your choice for tree ${row.label}`}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={option.current}
            disabled={choosing.isLocked}
            onClick={() => {
              pick(option.value)
            }}
          >
            {option.label}
          </button>
        ))}
      </div>
      <ChangeNote beforeCents={said} after={choosing.outcome.totalAmount} />
    </div>
  )
}

/**
 * "The total went down $940." Said once, after a choice.
 *
 * `beforeCents` is null until a choice is made. Both sides are integer cents
 * from `parseMoney`, the same parser every other dollar figure uses.
 */
function ChangeNote({
  beforeCents,
  after,
}: {
  beforeCents: number | null
  after: string | null
}) {
  if (beforeCents === null) return <p className="explorer-delta" aria-live="polite" />
  const delta = (parseMoney(after) ?? 0) - beforeCents
  const amount = wholeDollars(Math.abs(delta))
  let text = 'No change to the total.'
  if (delta < 0) text = `The total went down ${amount}, before tax.`
  if (delta > 0) text = `The total went up ${amount}, before tax.`
  return (
    <p className="explorer-delta" aria-live="polite">
      {text}
    </p>
  )
}
